import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { AgGridReact } from "ag-grid-react";
import Swal from "sweetalert2";
import {
  Trash2,
  Edit,
  PlusCircle,
  RotateCcw,
  ChevronUp,
} from "feather-icons-react/build/IconComponents";
import { setToogleHeader } from "../../core/redux/action";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { secureExportToExcel } from "../../utils/secureExcelUtils";
import { fetchPrintOrderList, deletePrintOrder } from "../../core/api/products";
import PrintOrderDetails from "./printorderdetails";
import { message } from "antd";

const PrintOrdersList = () => {
  const [orders, setOrders] = useState([]);
  const selectedClients = useSelector((state) => state.selectedClients);
  const [clientId, setClientId] = useState(null);
  const [isOrderDetailsVisible, setOrderDetailsVisible] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [sidebarWidth, setSidebarWidth] = useState(0);
  const sidebarRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5000);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const gridRef = useRef(null);
  const gridApiRef = useRef(null);
  // Ref to track in-flight request key
  const pendingRequestRef = useRef(null);
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);

  const renderExcelTooltip = (props) => (
    <Tooltip id="excel-tooltip" {...props}>
      Excel
    </Tooltip>
  );
  
  const renderRefreshTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Refresh
    </Tooltip>
  );
  
  const renderCollapseTooltip = (props) => (
    <Tooltip id="collapse-tooltip" {...props}>
      Collapse
    </Tooltip>
  );

  useEffect(() => {
    fetchOrderData();
  }, [selectedClients]);

  const fetchOrderData = async (page = 1, pageSize = 5000) => {
    // Validate and deduplicate
    const validPage = typeof page === "number" && !isNaN(page) ? page : 1;
    const validPageSize = typeof pageSize === "number" && !isNaN(pageSize) ? pageSize : 5000;

    const requestKey = `${selectedClients.join(',')}-${validPage}-${validPageSize}`;
    if (pendingRequestRef.current === requestKey || loading) {
      console.log('Skipping duplicate or in-progress print orders request', requestKey);
      return;
    }
    pendingRequestRef.current = requestKey;

    // duplicates of validPage and validPageSize removed to avoid redeclaration

    setLoading(true);
    if (gridApiRef.current?.api) gridApiRef.current.api.showLoadingOverlay();

    try {

      const response = await fetchPrintOrderList(selectedClients, validPage, validPageSize);

      console.log("aaa:", response);
      const indexedOrders = response.data.results.map((item, idx) => ({
        ...item,
        index: (validPage - 1) * validPageSize + idx + 1,
      }));

      setOrders(indexedOrders);
      setTotalItems(response.data.count);
      setTotalPages(Math.ceil(response.data.count / validPageSize));
      setPage(validPage);
      setPageSize(validPageSize);
    } catch (error) {
      console.error("Error fetching print orders:", error);
      message.error("Failed to load print orders. Please try again.");
      setOrders([]);
    } finally {
      // Clear pending flag
      if (pendingRequestRef.current === requestKey) {
        pendingRequestRef.current = null;
      }
      setLoading(false);
      if (gridApiRef.current?.api) gridApiRef.current.api.hideOverlay();
    }
  };

  const handlePageChange = (newPage, newPageSize) => {
    fetchOrderData(newPage, newPageSize);
  };

  const handleCreateOrder = () => {
    if (selectedClients.length === 0) {
      message.warning("Please select a client.");
      return;
    }
    if (selectedClients.length > 1 ) {
      message.warning("Please select one client.");
      return;
    }
    setClientId(selectedClients[0]);
    setCurrentOrderId(null);
    setOrderDetailsVisible(true);
  };

  const handleCloseOrderDetails = () => {
    setOrderDetailsVisible(false);
    setCurrentOrderId(null);
  };

  const handleEditOrder = (orderId, _, brandId) => {
    setClientId(brandId);
    setCurrentOrderId(orderId);
    setOrderDetailsVisible(true);
  };

  const handleDeleteOrder = (orderId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deletePrintOrder(orderId);
          message.success("Print order deleted successfully");
          fetchOrderData(page, pageSize);
        } catch (error) {
          console.error("Error deleting print order:", error);
          message.error("Failed to delete print order");
        }
      }
    });
  };

  const valueGetter = (params) => params.data[params.colDef.field];
  const cellRenderer = ({ value }) => (value === null || value === undefined ? "-" : value);
  const dateFormatter = ({ value }) => (!value ? "-" : new Date(value).toLocaleDateString());
  const priceFormatter = ({ value }) => (value === null || value === undefined ? "-" : `$${parseFloat(value).toFixed(2)}`);

  const actionsRenderer = (params) => (
    <div className="action-table-data edit-delete-action d-flex gap-1">
      <a className="p-1" title="Edit Order" onClick={() => handleEditOrder(params.data.print_order_id, null, params.data.brand_id)}>
        <Edit className="feather-edit" style={{ width: 16, height: 16 }} />
      </a>
      <a className="p-1 confirm-text" title="Delete Order" onClick={() => handleDeleteOrder(params.data.print_order_id)}>
        <Trash2 className="feather-delete" style={{ width: 16, height: 16 }} />
      </a>
    </div>
  );

  const columnDefs = [
    { headerName: "#", field: "index", valueGetter, width: 70, sortable: false, filter: false, resizable: true },
    { headerName: "Invoice Number", field: "invoice_number", valueGetter, cellRenderer, width: 160 },
    { headerName: "Invoice Date", field: "invoice_date", valueGetter, cellRenderer: dateFormatter, width: 140 },
    { headerName: "Brand", field: "brand_name", valueGetter, cellRenderer, width: 140 },
    { headerName: "Printer", field: "printer_name", valueGetter, cellRenderer, width: 140 },
    { headerName: "Code Offer", field: "code_offer", valueGetter, cellRenderer, width: 140 },
    { headerName: "Marketing File", field: "marketing_file_name", valueGetter, cellRenderer, width: 160 },
    { headerName: "Status", field: "status_name", valueGetter, cellRenderer, width: 140 },
    { headerName: "Quantity", field: "quantity", valueGetter, cellRenderer, width: 120 },
    { headerName: "Total Cost", field: "total_cost", valueGetter, cellRenderer: priceFormatter, width: 140 },
    { headerName: "Actions", field: "actions", cellRenderer: actionsRenderer, width: 100, sortable: false, filter: false },
  ];

  const exportToExcel = () => {
    if (!orders.length) {
      message.warning("There is no data to export");
      return;
    }
    const data = orders.map((order) => ({
      "#": order.index,
      "Invoice Number": order.invoice_number,
      "Invoice Date": new Date(order.invoice_date).toLocaleDateString(),
      "Brand": order.brand_name,
      "Printer": order.printer_name,
      "Code Offer": order.code_offer,
      "Marketing File": order.marketing_file_name || "",
      "Status": order.status_name,
      "Quantity": order.quantity,
      "Total Cost": `$${order.total_cost.toFixed(2)}`,
    }));
    secureExportToExcel(data, "Print_Orders_List");
  };

  const handleSearch = (e) => {
    if (gridApiRef.current?.api) {
      gridApiRef.current.api.setQuickFilter(e.target.value);
    }
    setSearchQuery(e.target.value);
  };

  return (
    <div className="p-1">
      <div className="page-header">
        <div className="add-item d-flex">
          <div className="page-title">
            <h4>Print Orders List</h4>
          </div>
        </div>
        <ul className="table-top-head">
          <div className="search-set">
            <div className="search-input">
              <input type="text" placeholder="Search" className="form-control form-control-sm formsearch" value={searchQuery} onChange={handleSearch} />
              <Link to="#" className="btn btn-searchset">
                <i data-feather="search" className="feather-search" />
              </Link>
            </div>
          </div>
          <li>
            <OverlayTrigger placement="top" overlay={renderExcelTooltip}>
              <Link to="#" onClick={exportToExcel}>
                <ImageWithBasePath src="assets/img/icons/xls.svg" alt="img" />
              </Link>
            </OverlayTrigger>
          </li>
          <li>
            <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
              <Link to="#" onClick={() => fetchOrderData(page, pageSize)}>
                <RotateCcw />
              </Link>
            </OverlayTrigger>
          </li>
          <li>
            <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
              <Link to="#" id="collapse-header" className={data ? "active" : ""} onClick={(e) => { e.preventDefault(); dispatch(setToogleHeader(!data)); }}>
                <ChevronUp />
              </Link>
            </OverlayTrigger>
          </li>
        </ul>
        <div className="page-btn d-flex align-items-center justify-content-between">
          <button className="btn btn-added" onClick={handleCreateOrder}>
            <PlusCircle className="me-2 iconsize" />
            Create New Print Order
          </button>
        </div>
      </div>

      <div className="table-responsive">
        <div className="ag-theme-quartz position-relative" style={{ height: "70vh", width: "100%" }}>
          {loading && (
            <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(255, 255, 255, 0.7)", zIndex: 1000, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
              <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <div style={{ marginTop: "15px", fontWeight: "bold" }}>Loading orders...</div>
            </div>
          )}
          <AgGridReact
            ref={gridRef}
            onGridReady={(params) => {
              gridApiRef.current = params;
              params.api.sizeColumnsToFit();
            }}
            rowData={orders}
            columnDefs={columnDefs}
            defaultColDef={{ flex: 1, minWidth: 50, sortable: true, filter: true, resizable: true }}
            suppressPaginationPanel={true}
            suppressRowTransform
            pagination
            paginationPageSize={pageSize}
            onFirstDataRendered={(params) => {
              params.api.sizeColumnsToFit();
            }}
          />
        </div>
        
        {/* Custom Pagination Footer */}
        <div className="row mt-3">
          <div className="col-md-6">
            <div className="pagination-info">
              Showing {orders.length > 0 ? (page - 1) * pageSize + 1 : 0} to {Math.min(page * pageSize, totalItems)} of {totalItems} entries
            </div>
          </div>
          <div className="col-md-6">
            <div className="d-flex justify-content-end gap-2">
              <div className="d-flex align-items-center me-3">
                <span className="me-2">Show</span>
                <select 
                  className="form-select form-select-sm" 
                  value={pageSize}
                  onChange={(e) => handlePageChange(1, parseInt(e.target.value))}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="500">500</option>
                  <option value="1000">1000</option>
                  <option value="2000">2000</option>
                  <option value="5000">5000</option>
                </select>
                <span className="ms-2">entries</span>
              </div>
              
              <div className="d-flex gap-1">
                <button 
                  onClick={() => handlePageChange(1, pageSize)}
                  disabled={page === 1}
                  style={{ padding: '5px 10px', border: '1px solid #ddd', borderRadius: '4px', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.5 : 1 }}
                >
                  First
                </button>
                <button 
                  onClick={() => handlePageChange(page - 1, pageSize)}
                  disabled={page === 1}
                  style={{ padding: '5px 10px', border: '1px solid #ddd', borderRadius: '4px', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.5 : 1 }}
                >
                  Prev
                </button>
                
                {/* Page numbers */}
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    // If 5 or fewer pages, show all
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    // If near start, show first 5
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    // If near end, show last 5
                    pageNum = totalPages - 4 + i;
                  } else {
                    // Otherwise show 2 before and 2 after current
                    pageNum = page - 2 + i;
                  }
                  
                  return (
                    <button 
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum, pageSize)}
                      style={{ 
                        padding: '5px 10px', 
                        border: '1px solid #ddd', 
                        borderRadius: '4px',
                        backgroundColor: page === pageNum ? '#007bff' : 'white',
                        color: page === pageNum ? 'white' : 'black'
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button 
                  onClick={() => handlePageChange(page + 1, pageSize)}
                  disabled={page === totalPages}
                  style={{ padding: '5px 10px', border: '1px solid #ddd', borderRadius: '4px', cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.5 : 1 }}
                >
                  Next
                </button>
                <button 
                  onClick={() => handlePageChange(totalPages, pageSize)}
                  disabled={page === totalPages}
                  style={{ padding: '5px 10px', border: '1px solid #ddd', borderRadius: '4px', cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.5 : 1 }}
                >
                  Last
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {clientId && (
        <PrintOrderDetails
          ref={sidebarRef}
          isVisible={isOrderDetailsVisible}
          onClose={handleCloseOrderDetails}
          printOrderId={currentOrderId}
          onResize={setSidebarWidth}
          setPrintOrderId={setCurrentOrderId}
          fetchPrintOrderList={fetchOrderData}
          clientId={clientId}
        />
      )}
    </div>
  );
};
PrintOrdersList.displayName = "PrintOrdersList";

export default PrintOrdersList;
